import { Collapse } from '@mui/material';
import { styled } from '@mui/system';
import { fm } from 'Components/FormatMessage';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import FormSectionBar from 'Components/FormSectionBar';
import FormSectionHistoryAcceptor from 'Components/FormSectionHistoryAcceptor';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import { ppa } from 'neuro-schemas';
import { equals } from 'ramda';
import * as React from 'react';
import { path } from 'Utility/ramdaReplacement';
import colors from '../../../../../config/theme/colors';
import { ReferenceText } from '../../../utils/styled';

import type { IFormContext } from 'Containers/FormContextHandler';
import type { IPPA_Evaluation_key } from 'neuro-schemas/ppa';

const {
  utils: { generalCriteriaFulfilled, resultPicker, PPAFormContent: sectionData, PPASummaryContent: summaryData },
} = ppa;

const SubHeader = styled('div')({
  margin: '1rem 0 2rem 0',
});

const MakeFormRowCounter = ({
  title,
  description,
  content,
  headerWidth,
}: {
  title: string;
  description?: React.JSX.Element;
  content: React.JSX.Element;
  headerWidth?: number;
}): React.JSX.Element => {
  return (
    <FormRow
      title={title}
      description={description}
      headerWidth={headerWidth ?? 6}
      headerStyle={{ fontWeight: 400, color: colors.primaryText }}
    >
      {content}
    </FormRow>
  );
};

const MakeFormRow = ({
  title,
  description,
  section,
  name,
  formData,
  editing,
  viewing,
  bottomMargin = true,
}: {
  title: string;
  description?: React.JSX.Element;
  section: string;
  name: string;
  formData: IFormData<IDocumentWithTypeV2<'ppa'>>;
  editing: boolean;
  viewing: boolean;
  bottomMargin?: boolean;
}): React.JSX.Element => (
  <FormRow title={title} description={description} headerWidth={6} bottomMargin={bottomMargin}>
    {editing ? (
      <InputHandler
        editing={!viewing}
        formData={{
          onChange: formData.onChange,
          document: {
            [section.split('.')[0]]: {
              [section.split('.')[1]]: {
                [name]: path([section.split('.')[0], section.split('.')[1], name], formData.document),
              },
            },
          },
        }}
        name={`${section}.${name}`}
        type="RadioButtonRow"
        preset="yesNoUnknown"
        height={3.5}
        width={9}
      />
    ) : (
      <span style={{ fontWeight: viewing ? undefined : 600 }}>
        {fm(
          `diagnosis.opts.yesNoUnknown.${
            name === 'criteriaForClinicalDiagnosisForNfvPPAFulfilled'
              ? resultPicker(formData.document, 'nonfluentVariantPPA', 'clinicalDiagnosis')
              : name === 'criteriaForClinicalDiagnosisForSemanticDementiaFulfilled'
                ? resultPicker(formData.document, 'semanticDementia', 'clinicalDiagnosis')
                : name === 'criteriaForClinicalDiagnosisForLogopenicProgressiveAphasiaFulfilled'
                  ? resultPicker(formData.document, 'logopenicProgressiveAphasia', 'clinicalDiagnosis')
                  : 'unknown'
          }`,
        )}
      </span>
    )}
  </FormRow>
);

const setAllUnknown = (formData: IFormContext['formData'], viewing: boolean): void => {
  if (!viewing) {
    // 🥵
    Object.keys(sectionData).forEach(section => {
      const subSectionData = sectionData[section as keyof typeof sectionData];
      Object.keys(subSectionData).forEach(subSection => {
        Object.keys(subSectionData[subSection as keyof typeof subSectionData])
          .filter(key => ['criteria', 'criteria2'].includes(key))
          .forEach((key: string) => {
            (subSectionData[subSection as keyof typeof subSectionData][key] as string[]).forEach(key => {
              const document = formData.document as { [key: string]: any };
              if (
                (!document[section] || !document[section][subSection] || !document[section][subSection][key]) &&
                ![
                  'criteriaForClinicalDiagnosisForNfvPPAFulfilled',
                  'criteriaForClinicalDiagnosisForSemanticDementiaFulfilled',
                  'criteriaForClinicalDiagnosisForLogopenicProgressiveAphasiaFulfilled',
                ].includes(key)
              ) {
                if (!equals(path([section, subSection, key], document) ?? '', 'unknown')) {
                  formData.onChange?.({
                    [section]: {
                      ...(path([section], document) as TAnyObject),
                      [subSection]: {
                        ...(path([section, subSection], document) as TAnyObject),
                        [key]: 'unknown',
                      },
                    },
                  });
                }
              }
            });
          });
      });
    });
  }
};

const PPAForm = ({
  documents,
  formData,
  editing,
  view,
}: IFormContext<IDocumentWithTypeV2<'ppa'>>): React.JSX.Element => {
  const [sectionOpen, setSectionOpen] = React.useState<{ [key: string]: boolean }>({
    nonfluentVariantPPA: true,
    semanticDementia: true,
    logopenicProgressiveAphasia: true,
  });

  const isViewing = !editing && !!view?.viewing;
  const docID = editing ?? view?.viewing;

  React.useEffect(() => {
    setAllUnknown(formData, isViewing);
  }, [formData, isViewing]);

  const toggleSection = (section: string): void => {
    const sectionState = { ...sectionOpen };
    sectionState[section] = !sectionState[section];
    setSectionOpen(sectionState);
  };

  return (
    <React.Fragment>
      {formData ? (
        <React.Fragment>
          <FormRow title="general.date">
            <InputHandler
              type="PartialDate"
              editing={!isViewing}
              name="date"
              formData={formData}
              dateDefault="now"
              isNotCancellable={true}
            />
          </FormRow>

          {Object.keys(sectionData).map((section, index): React.JSX.Element => {
            const subSectionData = sectionData[section as keyof typeof sectionData];
            const sectionContent = Object.keys(subSectionData)
              .filter(subSection => subSection !== 'summary')
              .map((subSection): React.JSX.Element => {
                const subHeaders = ['subHeader', 'subHeader2'].map((key: string) =>
                  subSectionData[subSection as keyof typeof subSectionData][key]
                    ? {
                        [key]: {
                          header: (
                            <SubHeader>
                              {fm(
                                `diagnosis.ppa.${section}.${subSection}.${subSectionData[subSection as keyof typeof subSectionData][key]}`,
                              )}
                            </SubHeader>
                          ),
                        },
                      }
                    : {},
                );
                const criteria = ['criteria', 'criteria2'].map((k: string) =>
                  subSectionData[subSection as keyof typeof subSectionData][k]
                    ? (subSectionData[subSection as keyof typeof subSectionData][k] as string[])?.reduce(
                        (current, key) => {
                          current[key] = {
                            element: (
                              <MakeFormRow
                                title={`diagnosis.ppa.${section}.${subSection}.${key}`}
                                section={`${section}.${subSection}`}
                                name={key}
                                formData={formData}
                                editing={
                                  [
                                    'criteriaForClinicalDiagnosisForNfvPPAFulfilled',
                                    'criteriaForClinicalDiagnosisForSemanticDementiaFulfilled',
                                    'criteriaForClinicalDiagnosisForLogopenicProgressiveAphasiaFulfilled',
                                  ].includes(key)
                                    ? false
                                    : true
                                }
                                viewing={isViewing}
                                description={
                                  [
                                    'criteriaForClinicalDiagnosisForNfvPPAFulfilled',
                                    'criteriaForClinicalDiagnosisForSemanticDementiaFulfilled',
                                    'criteriaForClinicalDiagnosisForLogopenicProgressiveAphasiaFulfilled',
                                  ].includes(key)
                                    ? fm(`diagnosis.ppa.autoFillDescription`)
                                    : undefined
                                }
                              />
                            ),
                          };
                          return current;
                        },
                        {} as { [key: string]: TAnyObject },
                      )
                    : {},
                );
                const results = ['result', 'result2'].map((k: string) =>
                  subSectionData[subSection as keyof typeof subSectionData][k]
                    ? (subSectionData[subSection as keyof typeof subSectionData][k] as string[])?.reduce(
                        (current, key) => {
                          current[key] = {
                            element: (
                              <MakeFormRowCounter
                                title={`diagnosis.ppa.${section}.${subSection}.${key}`}
                                content={
                                  <div style={{ fontWeight: 600, marginBottom: '4.5rem' }}>
                                    {fm(
                                      `diagnosis.opts.yesNoUnknown.${resultPicker(
                                        formData.document,
                                        section as keyof typeof sectionData,
                                        subSection as keyof typeof subSectionData,
                                        key as IPPA_Evaluation_key,
                                      )}`,
                                    )}
                                  </div>
                                }
                              />
                            ),
                          };
                          return current;
                        },
                        {} as { [key: string]: TAnyObject },
                      )
                    : {},
                );

                const structure = [subHeaders[0], criteria[0], subHeaders[1], criteria[1], results[0]];

                const subSectionContent = Object.assign({}, ...structure);

                return (
                  <React.Fragment key={section + subSection}>
                    <FormSectionHistoryAcceptor
                      name={`${section}.${subSection}`}
                      formData={formData}
                      documentID={docID}
                      documents={documents}
                      optionFormatter={s => fm(`general.${s}`)}
                      header={`diagnosis.ppa.${section}.${subSection}.title`}
                      hideCopyButton={isViewing}
                    >
                      {subSectionContent}
                    </FormSectionHistoryAcceptor>
                  </React.Fragment>
                );
              });
            return (
              <React.Fragment key={section}>
                <div style={{ cursor: index > 0 ? 'pointer' : undefined }} onClick={(): void => toggleSection(section)}>
                  <FormSectionBar header={`diagnosis.ppa.${section}.title`} open={sectionOpen[section]} />
                </div>
                {index > 0 ? (
                  <Collapse in={sectionOpen[section]} collapsedSize={'2rem'}>
                    {sectionContent}
                  </Collapse>
                ) : (
                  sectionContent
                )}
              </React.Fragment>
            );
          })}

          <FormSection header={'diagnosis.ppa.logopenicProgressiveAphasia.summary.title'}>
            {Object.keys(summaryData).map(key => {
              const summarySection = summaryData[key as keyof typeof summaryData];
              return (
                <React.Fragment key={key}>
                  {typeof summarySection === 'string' ? (
                    <SubHeader style={{ fontWeight: 600 }}>
                      {fm(`diagnosis.ppa.logopenicProgressiveAphasia.summary.${key}`)}
                    </SubHeader>
                  ) : (
                    <React.Fragment>
                      {summarySection.map(k => {
                        return (
                          <MakeFormRowCounter
                            key={k}
                            title={`diagnosis.ppa.logopenicProgressiveAphasia.summary.${k}`}
                            content={
                              <div style={{ fontWeight: 600 }}>
                                {fm(
                                  `diagnosis.opts.yesNoUnknown.${resultPicker(
                                    formData.document,
                                    '_summary',
                                    undefined,
                                    k,
                                  )}`,
                                )}
                              </div>
                            }
                            headerWidth={5}
                          />
                        );
                      })}
                    </React.Fragment>
                  )}
                </React.Fragment>
              );
            })}
          </FormSection>
          {!(['result', 'result2', 'result3'] as const).some(key => {
            return (
              summaryData[key].filter(k => resultPicker(formData.document, '_summary', undefined, k) === 'yes').length >
              0
            );
          }) && (
            <FormRow
              title={'diagnosis.ppa.logopenicProgressiveAphasia.summary.generalCriteriaForPPAFulfilled'}
              headerWidth={5}
              headerStyle={{ fontWeight: 600, color: colors.primaryText, margin: '2rem 0 2rem 0' }}
            >
              <div style={{ fontWeight: 600, margin: '2rem 0 2rem 0' }}>
                {fm(`diagnosis.opts.yesNoUnknown.${generalCriteriaFulfilled(formData.document)}`)}
              </div>
            </FormRow>
          )}

          <Container>
            <Item style={{ padding: '2rem 0' }}>
              <ReferenceText>{fm('diagnosis.ppa.referenceText')}</ReferenceText>
            </Item>
          </Container>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default withFormContext(PPAForm);
